// Server-configurable settings loader
interface AppLimits {
  maxFreeFileSize: number
  maxConcurrentFiles: number
  maxFreeSignaturesPerProcess: number
  maxPaidFileSize: number
  maxTotalProcessSize: number
}

interface AppConfig {
  apiBaseUrl: string
  commercialSignaturesUrl: string
  longtermPlansUrl: string
  localesPath: string
  limits: AppLimits
  statusPollingIntervalMs: number
  deeplinkAppName?: string
  termsOfServiceUrl?: string
  serviceTermsUrl?: string
  privacyPolicyUrl?: string
  companyUrl?: string
  testHash?: string
  cencertLogoUrl?: string
  commercialSectionEnabled?: boolean
  reloadProtectionEnabled?: boolean
  showAdditionalLogoInHeader?: boolean
  additionalLogoUrl?: string
  hashProcessShowSuccessView: boolean
  hashProcessRedirectDelaySeconds: number
  processErrorRedirectDelaySeconds: number
  requireTokenForAccess?: boolean
  graphicSignatureEnabled?: boolean
}

// Default configuration used as fallback for missing keys
const defaultConfig: AppConfig = {
  apiBaseUrl: '/api',
  commercialSignaturesUrl:
    'https://www.cencert.pl/podpis-kwalifikowany-na-karcie/',
  longtermPlansUrl: 'https://www.cencert.pl/podpis-kwalifikowany-na-karcie/',
  localesPath: '/locales',
  statusPollingIntervalMs: 5000, // 5 seconds default
  limits: {
    maxFreeFileSize: 5242880, // 5MB in bytes
    maxConcurrentFiles: 5,
    maxFreeSignaturesPerProcess: 1,
    maxPaidFileSize: 52428800, // 50MB in bytes
    maxTotalProcessSize: 104857600, // 100MB in bytes
  },
  commercialSectionEnabled: true,
  reloadProtectionEnabled: true, // Enable reload protection by default
  serviceTermsUrl: 'https://www.cencert.pl/warunki-korzystania-z-uslug-zaufania/',
  showAdditionalLogoInHeader: false,
  hashProcessShowSuccessView: true, // Show success view before redirect for HASH processes
  hashProcessRedirectDelaySeconds: 10, // Auto-redirect delay in seconds (0 = no auto-redirect)
  processErrorRedirectDelaySeconds: 10, // Auto-redirect delay in seconds on process error page
  requireTokenForAccess: false, // Require valid token to access the application
  graphicSignatureEnabled: true, // Enable graphic signature feature (Step 2 in AddDocumentDialog)
}

let cachedConfig: AppConfig | null = null

export async function loadConfig(): Promise<AppConfig> {
  if (cachedConfig) {
    return cachedConfig
  }

  try {
    const response = await fetch('/config.json')
    if (!response.ok) {
      throw new Error(`Failed to load config: ${response.status}`)
    }

    const loadedConfig = await response.json()

    cachedConfig = {
      ...defaultConfig,
      ...loadedConfig,
      limits: {
        ...defaultConfig.limits,
        ...(loadedConfig.limits || {}),
      },
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return cachedConfig!
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error loading config:', error)

    return defaultConfig
  }
}

export function getConfig(): AppConfig {
  if (!cachedConfig) {
    throw new Error('Config not loaded. Call loadConfig() first.')
  }
  return cachedConfig
}
